import { useState } from "react";
import { useForm } from "react-hook-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import ConfirmationDialog from "../../shared/ConfirmationDialog";
import {
    createField,
    deactivateField,
    deleteField,
    exportFieldCatalog,
    listDocumentTypes,
    listFields,
    updateField,
} from "../../../utils/cdesApi";
import FieldFormDialog from "./FieldFormDialog";
import FieldTable from "./FieldTable";
import FieldToolbar from "./FieldToolbar";
import ImportFieldsDialog from "./ImportFieldsDialog";
import "./CdesGrid.scss";
import "./FieldAdmin.scss";

const emptyFilters = {
    q: "",
    category: "",
    dataType: "",
    scope: "",
    universal: "",
    documentType: "",
    includeInactive: false,
};

const FieldView = () => {
    const queryClient = useQueryClient();
    const inactiveForm = useForm({ defaultValues: { includeInactive: false } });
    const [filters, setFilters] = useState(emptyFilters);
    const [page, setPage] = useState(0);
    const [formMode, setFormMode] = useState(null);
    const [editingField, setEditingField] = useState(null);
    const [formError, setFormError] = useState("");
    const [saving, setSaving] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [deactivateTarget, setDeactivateTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [reactivatingKey, setReactivatingKey] = useState("");
    const [exporting, setExporting] = useState(false);

    const fieldsQuery = useQuery({
        queryKey: ["cdes-fields", filters],
        queryFn: () => listFields(filters),
    });
    const documentTypesQuery = useQuery({
        queryKey: ["cdes-document-types"],
        queryFn: listDocumentTypes,
    });

    const fields = fieldsQuery.data?.fields ?? [];
    const documentTypes = documentTypesQuery.data?.documentTypes ?? [];
    const typeNames = new Map(documentTypes.map((documentType) => [documentType.documentTypeKey, documentType.displayName]));

    const setFilter = (name, value) => {
        setPage(0);
        setFilters((current) => ({ ...current, [name]: value }));
    };

    const clearFilters = () => {
        setPage(0);
        setFilters(emptyFilters);
        inactiveForm.reset({ includeInactive: false });
    };

    const refresh = () =>
        Promise.all([
            queryClient.invalidateQueries({ queryKey: ["cdes-fields"] }),
            queryClient.invalidateQueries({ queryKey: ["cdes-document-types"] }),
            queryClient.invalidateQueries({ queryKey: ["cdes-field-columns"] }),
        ]);

    const openCreate = () => {
        setFormError("");
        setEditingField(null);
        setFormMode("create");
    };

    const openEdit = (field) => {
        setFormError("");
        setEditingField(field);
        setFormMode("edit");
    };

    const closeForm = () => {
        if (saving) return;
        setFormMode(null);
        setEditingField(null);
        setFormError("");
    };

    const submitForm = async (body) => {
        setSaving(true);
        setFormError("");
        try {
            if (formMode === "create") await createField(body);
            else await updateField(editingField.fieldKey, body);
            await refresh();
            toast.success(formMode === "create" ? "Field created." : "Field updated.");
            setFormMode(null);
            setEditingField(null);
        } catch (error) {
            setFormError(error.message || "Unable to save the field.");
            throw error;
        } finally {
            setSaving(false);
        }
    };

    const confirmDeactivate = async () => {
        try {
            await deactivateField(deactivateTarget.fieldKey);
            await refresh();
            toast.success("Field deactivated.");
        } catch (error) {
            toast.error(error.message || "Unable to deactivate the field.");
            throw error;
        }
    };

    const confirmDelete = async () => {
        try {
            await deleteField(deleteTarget.fieldKey);
            await refresh();
            toast.success("Field deleted.");
        } catch (error) {
            toast.error(error.message || "Unable to delete the field.");
            throw error;
        }
    };

    const reactivate = async (field) => {
        setReactivatingKey(field.fieldKey);
        try {
            await updateField(field.fieldKey, { status: "ACTIVE" });
            await refresh();
            toast.success("Field reactivated.");
        } catch (error) {
            toast.error(error.message || "Unable to reactivate the field.");
        } finally {
            setReactivatingKey("");
        }
    };

    const exportCatalog = async () => {
        setExporting(true);
        try {
            const document = await exportFieldCatalog();
            const blob = new Blob([JSON.stringify(document, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const anchor = window.document.createElement("a");
            anchor.href = url;
            anchor.download = "cdes-field-catalog.json";
            anchor.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            toast.error(error.message || "Unable to export the catalog.");
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="cdes-grid">
            <FieldToolbar
                filters={filters}
                documentTypes={documentTypes}
                documentTypesLoading={documentTypesQuery.isLoading}
                control={inactiveForm.control}
                exporting={exporting}
                onFilter={setFilter}
                onInactive={(checked) => setFilter("includeInactive", checked)}
                onClear={clearFilters}
                onCreate={openCreate}
                onImport={() => setImportOpen(true)}
                onExport={exportCatalog}
            />
            <FieldTable
                query={fieldsQuery}
                fields={fields}
                page={page}
                onPage={setPage}
                typeNames={typeNames}
                reactivatingKey={reactivatingKey}
                onEdit={openEdit}
                onDeactivate={setDeactivateTarget}
                onDelete={setDeleteTarget}
                onReactivate={reactivate}
            />
            {formMode ? (
                <FieldFormDialog
                    key={formMode === "edit" ? editingField.fieldKey : "create"}
                    field={editingField}
                    documentTypes={documentTypes}
                    saving={saving}
                    serverError={formError}
                    onClose={closeForm}
                    onSubmit={submitForm}
                />
            ) : null}
            {importOpen ? <ImportFieldsDialog onClose={() => setImportOpen(false)} onApplied={refresh} /> : null}
            <ConfirmationDialog
                open={Boolean(deactivateTarget)}
                onOpenChange={(open) => {
                    if (!open) setDeactivateTarget(null);
                }}
                title="Deactivate field"
                body={`${deactivateTarget?.displayName || "This field"} will become inactive and drop out of the Client and Plan views. Its configuration is kept.`}
                confirmLabel="Deactivate"
                confirmPendingLabel="Deactivating..."
                onConfirm={confirmDeactivate}
            />
            <ConfirmationDialog
                open={Boolean(deleteTarget)}
                onOpenChange={(open) => {
                    if (!open) setDeleteTarget(null);
                }}
                title="Delete field"
                body={`${deleteTarget?.displayName || "This field"} and its document-type settings will be permanently deleted when no reference record is found. This cannot be undone.`}
                confirmLabel="Delete"
                confirmPendingLabel="Deleting..."
                contentClassName="cdes-delete-confirm"
                onConfirm={confirmDelete}
            />
        </div>
    );
};

export default FieldView;
